import React, { memo, useState, useEffect } from 'react';
import { TrainingOverlayProps } from './types';
import { PreparingState } from './PreparingState';
import { ErrorState } from './ErrorState';

/**
 * TrainingOverlay Component
 *
 * Displays a blurred full-screen overlay during model training.
 * Shows preparing state while waiting for backend, then displays
 * RealTimeTrainingProgress once training data starts flowing.
 */
const TrainingOverlay: React.FC<TrainingOverlayProps> = memo(({
  isVisible,
  isPreparingData,
  children,
  connectionError
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Block body scroll when overlay is visible
  useEffect(() => {
    if (isVisible) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.top = originalTop;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isVisible]);

  // Track elapsed time for preparing data state
  useEffect(() => {
    if (!isVisible || !isPreparingData) {
      setElapsedSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, isPreparingData]);

  if (!isVisible) return null;

  const hasError = !!connectionError;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overscroll-none touch-none animate-fadeIn">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[6px] touch-none" />

      {/* Content */}
      <div className="relative z-10 w-[calc(100%-2rem)] sm:w-[90%] max-w-[700px] max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-2.5rem)] overflow-y-auto p-3 sm:p-5 animate-slideUp">
        {hasError ? (
          <ErrorState connectionError={connectionError!} />
        ) : isPreparingData ? (
          <PreparingState elapsedSeconds={elapsedSeconds} />
        ) : (
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden p-4 sm:p-6">
            {children}
          </div>
        )}
      </div>
    </div>
  );
});

TrainingOverlay.displayName = 'TrainingOverlay';

export default TrainingOverlay;
